import React, { useState } from 'react';
import { useAlerts } from '../context/AlertContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Zap, Sun, Droplets, MoveVertical, CarFront, Bell, Save } from 'lucide-react';

const Alertas = () => {
  const { alertSettings, saveAlertSettings } = useAlerts();
  const [localSettings, setLocalSettings] = useState(alertSettings);

  const handleSave = () => {
    saveAlertSettings(localSettings);
    toast.success('Configurações de alertas guardadas com sucesso!');
  };

  const updateCategorySetting = (categoria, campo, valor) => {
    setLocalSettings(prev => ({
      ...prev,
      [categoria]: {
        ...prev[categoria],
        [campo]: valor,
      },
    }));
  };

  const categorias = [
    {
      id: 'energia',
      nome: 'Energia',
      icon: Zap,
      cor: 'text-yellow-500',
      bgCor: 'bg-yellow-50',
      campos: [
        { key: 'desvioConsumo', label: 'Desvio de Consumo (%)', tipo: 'number', min: 1, max: 100 },
        { key: 'consumoMaximo', label: 'Consumo Máximo (kWh)', tipo: 'number', min: 100, max: 2000 },
      ],
    },
    {
      id: 'fotovoltaico',
      nome: 'Fotovoltaico',
      icon: Sun,
      cor: 'text-orange-500',
      bgCor: 'bg-orange-50',
      campos: [
        { key: 'producaoMinima', label: 'Produção Mínima (kWh)', tipo: 'number', min: 10, max: 500 },
        { key: 'eficienciaMinima', label: 'Eficiência Mínima (%)', tipo: 'number', min: 1, max: 100 },
      ],
    },
    {
      id: 'agua',
      nome: 'Água',
      icon: Droplets,
      cor: 'text-blue-500',
      bgCor: 'bg-blue-50',
      campos: [
        { key: 'desvioCaudal', label: 'Desvio de Consumo (%)', tipo: 'number', min: 5, max: 100 },
        { key: 'consumoMaximo', label: 'Consumo Máximo Diário (m³)', tipo: 'number', min: 1, max: 50 },
        { key: 'diasSemLeitura', label: 'Dias sem Leitura (alerta)', tipo: 'number', min: 1, max: 7 },
      ],
      dadosReais: true, // Indica que esta categoria usa dados reais
    },
    {
      id: 'elevadores',
      nome: 'Elevadores',
      icon: MoveVertical,
      cor: 'text-purple-500',
      bgCor: 'bg-purple-50',
      campos: [
        { key: 'tempoAvaria', label: 'Tempo Máximo de Avaria (min)', tipo: 'number', min: 5, max: 240 },
        { key: 'tempoParado', label: 'Tempo Máximo Parado (min)', tipo: 'number', min: 10, max: 480 },
      ],
    },
    {
      id: 'postosVE',
      nome: 'Postos VE',
      icon: CarFront,
      cor: 'text-green-500',
      bgCor: 'bg-green-50',
      campos: [
        { key: 'tempoInativo', label: 'Tempo Máximo Inativo (horas)', tipo: 'number', min: 1, max: 72 },
        { key: 'falhasConsecutivas', label: 'Falhas Consecutivas Máximas', tipo: 'number', min: 1, max: 10 },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Bell className="h-8 w-8 text-[#003D7A]" />
            Configuração de Alertas
          </h1>
          <p className="text-gray-600 mt-1">Defina os parâmetros que despoletam alertas no sistema</p>
        </div>
        <Button onClick={handleSave} className="bg-[#003D7A] hover:bg-[#002d5c]">
          <Save className="h-4 w-4 mr-2" />
          Guardar Configurações
        </Button>
      </div>

      <Tabs defaultValue="energia" className="w-full">
        <TabsList className="grid w-full grid-cols-5 h-auto">
          {categorias.map((cat) => {
            const Icon = cat.icon;
            return (
              <TabsTrigger key={cat.id} value={cat.id} className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-2">
                <Icon className={`h-4 w-4 ${cat.cor}`} />
                <span className="text-xs sm:text-sm">{cat.nome}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {categorias.map((cat) => {
          const Icon = cat.icon;
          return (
            <TabsContent key={cat.id} value={cat.id} className="mt-6">
              <Card>
                <CardHeader className={`${cat.bgCor} rounded-t-lg`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className={`h-6 w-6 ${cat.cor}`} />
                      <div>
                        <CardTitle className="text-lg">Alertas de {cat.nome}</CardTitle>
                        <CardDescription>Configure os limiares para alertas desta categoria</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`${cat.id}-enabled`} className="text-sm">Ativo</Label>
                      <Switch
                        id={`${cat.id}-enabled`}
                        checked={localSettings[cat.id]?.enabled ?? true}
                        onCheckedChange={(checked) => updateCategorySetting(cat.id, 'enabled', checked)}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {localSettings[cat.id]?.enabled ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {cat.campos.map((campo) => (
                        <div key={campo.key} className="space-y-2">
                          <Label htmlFor={`${cat.id}-${campo.key}`} className="text-sm font-medium">
                            {campo.label}
                          </Label>
                          <Input
                            id={`${cat.id}-${campo.key}`}
                            type={campo.tipo}
                            min={campo.min}
                            max={campo.max}
                            value={localSettings[cat.id]?.[campo.key] ?? ''}
                            onChange={(e) => updateCategorySetting(cat.id, campo.key, parseInt(e.target.value) || 0)}
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500">
                            Valor atual: {localSettings[cat.id]?.[campo.key]} | Intervalo: {campo.min} - {campo.max}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Bell className="h-12 w-12 mx-auto mb-4 opacity-30" />
                      <p>Alertas de {cat.nome} estão desativados</p>
                      <p className="text-sm">Ative o switch acima para configurar os parâmetros</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Resumo das Configurações */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Resumo das Configurações Atuais</CardTitle>
          <CardDescription>Visão geral de todos os parâmetros configurados</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {categorias.map((cat) => {
              const Icon = cat.icon;
              const isEnabled = localSettings[cat.id]?.enabled;
              return (
                <div key={cat.id} className={`p-4 rounded-lg border ${isEnabled ? cat.bgCor : 'bg-gray-50 opacity-60'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`h-5 w-5 ${isEnabled ? cat.cor : 'text-gray-400'}`} />
                    <span className="font-medium text-sm">{cat.nome}</span>
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                      {isEnabled ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  {isEnabled && (
                    <div className="space-y-1 text-xs text-gray-600">
                      {cat.campos.map((campo) => (
                        <div key={campo.key} className="flex justify-between">
                          <span>{campo.label.split(' (')[0]}:</span>
                          <span className="font-medium">{localSettings[cat.id]?.[campo.key]}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Alertas;
